import { memo } from 'react';
import { useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import type {
    BaseProductData,
    ProductSibling,
    ProductVariant,
} from '../../types';
import combineAttributes from '../../util/combineAttributes';
import CurrentStoreLink from '../../../core/components/router/CurrentStoreLink';
import notEmpty from '../../../core/util/notEmpty';
import ProductImage from '../ProductImage/ProductImage';
import styles from './ProductDetails.module.scss';

export const processSiblings = (
    siblings: ProductSibling[],
    selectedVariant?: ProductVariant
): (ProductSibling & {
    newVariantId?: string;
})[] => {
    const attributeCheckerFn = ({
        name,
        value,
    }: {
        name: string;
        value: string;
    }) => {
        return selectedVariant?.variationAttributes?.find(
            attribute => attribute.name === name && attribute.value === value
        );
    };

    return siblings.map(sibling => {
        const newVariantId = sibling.variants.find(variant => {
            return variant.variationAttributes?.every(attributeCheckerFn);
        })?.id;

        return {
            ...sibling,
            newVariantId,
        };
    });
};

export const getVariationAttributeNames = (
    processedSiblings: BaseProductData[]
) =>
    Array.from(
        new Set(
            processedSiblings.flatMap(({ variants }) =>
                variants.flatMap(({ variationAttributes: items }) =>
                    items?.map(attribute => attribute.name)
                )
            )
        )
    ).filter(notEmpty);

export const ProductSiblingItem = memo(
    ({
        id,
        media,
        siblingAttributes,
        newVariantId,
    }: ProductSibling & {
        newVariantId?: string;
    }) => {
        const { pid } = useParams<{
            pid: string;
        }>();

        let targetLink = `/product/${id}`;
        if (newVariantId) {
            targetLink += `/${newVariantId}`;
        }

        return (
            <CurrentStoreLink
                data-test-class="colorVariant"
                data-test-state={pid === id ? 'active' : 'inactive'}
                key={id}
                className={clsx(styles.productColorItem, {
                    [styles.productColorItemSelected]: pid === id,
                })}
                to={targetLink}
            >
                <ProductImage
                    renditionSize="mini"
                    url={media[0]?.renditionUrl}
                    alt={combineAttributes(siblingAttributes ?? []).value}
                    overviewPlacement="top"
                />
            </CurrentStoreLink>
        );
    }
);

const SiblingOrVariantItems = ({
    names,
    items,
}: {
    names?: string[];
    items?: {
        name: string;
        value: string;
    }[];
}) =>
    items ? (
        <>
            {items.map(({ name, value }) => (
                <div key={`${name}-${value}`}>
                    {name}: <span>{value}</span>
                </div>
            ))}
        </>
    ) : (
        <>{names?.map(name => <div key={name}>{name}</div>)}</>
    );

export default memo(SiblingOrVariantItems);
